import { Group } from 'three';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { loadGLTF, findSkinnedMesh } from './gltf.js';
import { loadClipPack } from './clipPack.js';
import { createAnimator } from './animator.js';
import { retargetClips, retargetPosedClip } from './retarget.js';
import { createPlaceholderFighter } from './placeholder.js';
import { contactSamples, createGrounding } from './grounding.js';

/**
 * Mixamo authors its characters facing +Z; this rig's forward is its own -Z.
 * Every clip is turned through half a circle on the way in so the two agree —
 * the FBX library, the GLB packs and any clip shipped inside a model alike —
 * which is cheaper and far less error-prone than teaching the game two
 * conventions. Until step 74 only the FBX path turned, and the placeholder
 * pack, authored facing -Z to make up for it, put a real body backwards.
 */
const MIXAMO_YAW = Math.PI;

/**
 * Clips whose horizontal travel belongs to the game rather than to the
 * animation. A walk cycle that carried its own stride would fight
 * `locomotion.js` for control of where the fighter is, and a knockout that
 * slid a metre sideways would leave the body somewhere the rules think it is
 * not. Everything else keeps its travel: a jab should lunge.
 */
const IN_PLACE = new Set(['walking', 'walkingBackwards', 'fightingIdle', 'knockedOut']);

/**
 * Loads a rigged fighter and its animation packs.
 *
 * `modelUrl` is a GLB carrying the skinned mesh and skeleton. Each entry in
 * `animationUrls` is an animation-only file whose clips are retargeted onto
 * that skeleton, which is how a shared move set gets reused across fighters.
 *
 * Two formats land here. A `.glb` pack carries named clips on a rig that
 * shares this one's rest pose, and goes through the cheap name-based
 * retarget — with the same half turn as everything else. A `.fbx` is one Mixamo clip, named "mixamo.com" inside whatever
 * the file is called, on a rig whose bones point somewhere else entirely — it
 * takes its name from the filename and the rest-compensating retarget.
 *
 * Nothing here throws. A missing or unrigged model falls back to `fallbackUrl`
 * if there is one — the placeholder mannequin, which ships with the game and
 * has the same skeleton — and to the capsule if that fails too, so a bad asset
 * degrades the picture rather than blanking it. Either fallback warns, by name:
 * a fighter quietly drawn as a mannequin is a bug that looks like a feature.
 */
export async function loadFighter({
  modelUrl,
  fallbackUrl = null,
  animationUrls = [],
  name = 'fighter',
  /**
   * Where the body goes. A fresh group by default; a combatant changing bodies
   * passes its own, so everything holding that group — the scene, the arena
   * host, the camera — is still holding the fighter afterwards.
   */
  root = null,
  /**
   * Whether the body goes in now. `false` builds it detached, for a caller
   * that swaps one body for another in a single step: `attach()` the new one,
   * `dispose()` the old, and no frame ever draws both or neither.
   */
  attach = true,
  onWarn = (message) => console.warn(`[fighter] ${message}`),
} = {}) {
  const object3D = root ?? new Group();
  if (!root) object3D.name = name;

  // Every file this fighter needs, asked for **now**, together. The body used
  // to be fetched and parsed before the first clip pack was even requested,
  // and each pack after the one before it — three round trips in a row on the
  // way to the title screen (step 95). The caches make the later awaits free.
  for (const url of animationUrls) prefetchClips(url);

  let model = modelUrl ? await tryLoad(() => loadModel(modelUrl), onWarn) : null;
  let skinnedMesh = model ? findSkinnedMesh(model.scene) : null;
  let source = skinnedMesh ? modelUrl : null;
  if (!skinnedMesh && modelUrl && model) onWarn(`${modelUrl} has no SkinnedMesh`);

  if (!skinnedMesh && fallbackUrl && fallbackUrl !== modelUrl) {
    onWarn(`${modelUrl} did not give a rigged fighter; falling back to ${fallbackUrl}`);
    model = await tryLoad(() => loadModel(fallbackUrl), onWarn);
    skinnedMesh = model ? findSkinnedMesh(model.scene) : null;
    source = skinnedMesh ? fallbackUrl : null;
  }

  if (!skinnedMesh) {
    if (modelUrl) onWarn('no rigged fighter could be loaded; drawing the capsule');
    const body = createPlaceholderFighter();
    return fighter({ object3D, body, attach, skeleton: null, animator: null, loaded: false, source: null });
  }

  // A clone of the cached file, never the file itself: two fighters in the
  // same body need two skeletons, and a later booking must find the original
  // unposed. Geometry and textures are shared; bones, meshes and the skeleton
  // are this fighter's own.
  const body = cloneSkinned(model.scene);
  skinnedMesh = findSkinnedMesh(body);
  body.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
      // Limbs leave the original bounding box the moment the rig moves.
      child.frustumCulled = false;
    }
  });
  // After the animator, every frame: the body's lowest point held out of the
  // floor, whatever its build. See `grounding.js`. The samples depend only on
  // the mesh, so a body seen before costs nothing to ground.
  const grounding = createGrounding({
    root: object3D,
    model: body,
    mesh: skinnedMesh,
    samples: cached(samplesBySource, source, () => contactSamples(skinnedMesh)),
  });

  const skeleton = skinnedMesh.skeleton;
  const clips = await clipsFor(model, source, skeleton, animationUrls, onWarn);
  const animator = createAnimator(body, []);
  const added = animator.addClips(clips);
  if (added < clips.length) onWarn(`${clips.length - added} clip(s) had no bindable tracks`);

  return fighter({ object3D, body, attach, skeleton, animator, loaded: true, source, grounding });
}

/**
 * Parsed models, by URL. Fighting the same opponent twice must not fetch and
 * parse their body twice — and both corners wear the default body, so even
 * the first round shares one parse between them.
 */
const models = new Map();
const clipsBySource = new Map();
const samplesBySource = new Map();

function loadModel(url) {
  if (!models.has(url)) {
    const pending = loadGLTF(url);
    // A failure is not remembered: a retry should be allowed to fetch again.
    pending.catch(() => models.delete(url));
    models.set(url, pending);
  }
  return models.get(url);
}

function cached(map, key, make) {
  if (!map.has(key)) map.set(key, make());
  return map.get(key);
}

function cachedAsync(map, key, make) {
  if (!map.has(key)) {
    const pending = make();
    pending.catch(() => map.delete(key));
    map.set(key, pending);
  }
  return map.get(key);
}

/**
 * A body's clips. Retargeting reads only the bind pose, which every clone of
 * one file shares, so the clips are made once per body and reused by every
 * fighter who wears it — and by every booking after the first.
 */
function clipsFor(model, source, skeleton, animationUrls, onWarn) {
  return cachedAsync(clipsBySource, `${source}|${animationUrls.join('|')}`, async () => {
    const list = retargetClips(model.animations, skeleton, { yaw: MIXAMO_YAW, onWarn });
    for (const url of animationUrls) {
      const retargeted = /\.json$/i.test(url)
        ? await loadPackClips(url, skeleton, onWarn)
        : /\.fbx$/i.test(url)
          ? await loadFBXClips(url, skeleton, onWarn)
          : await loadGLBClips(url, skeleton, onWarn);
      list.push(...retargeted);
    }
    return list;
  });
}

/**
 * Warms a body before anybody wears it (step 95): fetched, parsed, and its
 * clips retargeted, into the same caches `loadFighter` reads — so the booking
 * that follows costs a clone and nothing else. The hub calls it for all three
 * cards while the player is still choosing. Never throws; resolves to whether
 * the body is ready.
 */
export async function preloadFighter({ modelUrl, animationUrls = [], onWarn = () => {} } = {}) {
  if (!modelUrl) return false;
  const model = await tryLoad(() => loadModel(modelUrl), onWarn);
  const mesh = model ? findSkinnedMesh(model.scene) : null;
  if (!mesh) return false;
  await clipsFor(model, modelUrl, mesh.skeleton, animationUrls, onWarn);
  return true;
}

/** Forgets every cached body and clip set. For tests that need a cold load. */
export function clearFighterCache() {
  models.clear();
  clipsBySource.clear();
  samplesBySource.clear();
}

async function loadGLBClips(url, skeleton, onWarn) {
  // Through the model cache: a clip pack is the same file for every body, and
  // fetching it again for each new one was 76 kB a booking for nothing.
  const pack = await tryLoad(() => loadModel(url), onWarn);
  if (!pack) return [];
  if (!pack.animations.length) {
    onWarn(`${url} contains no animation clips`);
    return [];
  }
  return retargetClips(pack.animations, skeleton, {
    sourceSkeleton: findSkinnedMesh(pack.scene)?.skeleton,
    yaw: MIXAMO_YAW,
    onWarn,
  });
}

/**
 * One Mixamo FBX becomes one clip. The file holds no mesh, so the source rig
 * is reached through its bones, and every clip inside is called "mixamo.com"
 * — the filename is the only place the clip's identity is recorded.
 */
async function loadFBXClips(url, skeleton, onWarn) {
  // Imported when asked for: the game ships the packed library (`clips.json`)
  // and never loads an FBX, so FBXLoader stays out of the bundle it downloads.
  const { loadFBX, findRootBone, clipNameFromUrl } = await import('./fbx.js');
  const pack = await tryLoad(() => loadFBX(url), onWarn);
  if (!pack) return [];

  const clip = pack.animations[0];
  if (!clip) {
    onWarn(`${url} contains no animation clips`);
    return [];
  }
  if (pack.animations.length > 1) {
    onWarn(`${url} holds ${pack.animations.length} clips; keeping the first`);
  }

  const sourceRoot = findRootBone(pack);
  if (!sourceRoot) {
    onWarn(`${url} has no skeleton to retarget from`);
    return [];
  }

  const name = clipNameFromUrl(url);
  return [
    retargetPosedClip(clip, skeleton, {
      sourceRoot,
      name,
      yaw: MIXAMO_YAW,
      rootMotion: IN_PLACE.has(name) ? 'inPlace' : 'offset',
      onWarn,
    }),
  ];
}

/** Starts a clip file downloading into its cache, without waiting for it. */
function prefetchClips(url) {
  const pending = /\.json$/i.test(url) ? loadClipPack(url) : /\.glb$/i.test(url) ? loadModel(url) : null;
  pending?.catch(() => {});
}

/**
 * The packed library (`clipPack.js`): fetched and unpacked once, then each
 * clip retargeted onto this body exactly as an FBX one would be.
 */
async function loadPackClips(url, skeleton, onWarn) {
  const pack = await tryLoad(() => loadClipPack(url), onWarn);
  if (!pack) return [];
  if (!pack.length) {
    onWarn(`${url} contains no animation clips`);
    return [];
  }
  return pack.map(({ name, clip, sourceRoot }) =>
    retargetPosedClip(clip, skeleton, {
      sourceRoot,
      name,
      yaw: MIXAMO_YAW,
      rootMotion: IN_PLACE.has(name) ? 'inPlace' : 'offset',
      onWarn,
    })
  );
}

async function tryLoad(load, onWarn) {
  try {
    return await load();
  } catch (error) {
    onWarn(error.message);
    return null;
  }
}

/** The handle the rest of the game holds. Animator calls are safe when null. */
function fighter({ object3D, body, attach, skeleton, animator, loaded, source, grounding = null }) {
  let attached = false;
  const handle = {
    object3D,
    /** The model itself — what `attach` puts into `object3D`. */
    body,
    skeleton,
    animator,
    loaded,
    /** The file actually drawn — `modelUrl`, the fallback, or null for the capsule. */
    source,
    /** The lift holding the body out of the floor, or null for the capsule. */
    grounding,
    clipNames: () => animator?.names() ?? [],
    get currentClip() {
      return animator?.current ?? null;
    },
    play: (clipName, options) => animator?.play(clipName, options) ?? false,
    update: (dt) => {
      animator?.update(dt);
      grounding?.update();
    },
    attach() {
      if (!attached) object3D.add(body);
      attached = true;
      return handle;
    },
    /** Takes the body out and lets its mixer and skeleton go. Shared geometry stays cached. */
    dispose() {
      animator?.dispose();
      body.removeFromParent();
      body.traverse((child) => {
        if (child.isSkinnedMesh) child.skeleton?.dispose();
      });
      attached = false;
    },
  };
  if (attach) handle.attach();
  return handle;
}
